/**
 * Component: Layout
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01, FRONTEND-WEB-V1-R10, FRONTEND-WEB-V1-R11,
 *               FRONTEND-WEB-V1-R12, FRONTEND-WEB-V1-R13, FRONTEND-WEB-V1-R14,
 *               FRONTEND-WEB-V1-R15, BROWSE-V1-R02
 *
 * Main layout component with responsive design and accessibility features.
 * FRONTEND-WEB-V1-R01: Navigation to all four primary interaction modes.
 * FRONTEND-WEB-V1-R10: Theme toggle in header for light/dark mode.
 * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules via configuration feature flags.
 * FRONTEND-WEB-V1-R12: Fully hide disabled modules rather than grey them out.
 * FRONTEND-WEB-V1-R13: Fully responsive design supporting desktop, tablet, and mobile.
 * FRONTEND-WEB-V1-R15: Full keyboard navigation support.
 * BROWSE-V1-R02: When enabled, the system presents a Browse navigation entry
 *                alongside Chat and the Search modes. Gated on flags.browse,
 *                which mirrors the deployment-time browse_enabled setting via
 *                FeatureFlagContext (BROWSE-V1-R01).
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, MessageSquareText, SearchCheck, BrainCircuit, Search, BookOpen, Maximize2, Minimize2, Settings, Users, FileText, ShieldAlert, BarChart3, LogOut, ScrollText, Activity, FileBarChart, Mic, type LucideIcon } from 'lucide-react';
import { useBranding, useFeatureFlags, useTheme } from '@/contexts';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useUser, useAuthStore } from '@/stores/auth-store';
import type { FeatureFlags } from '@/types';

/**
 * Navigation item configuration
 */
interface NavigationItem {
  /** Display label for the nav item */
  label: string;
  /** Route path */
  href: string;
  /** Feature flag key that controls this item's visibility */
  featureKey: keyof FeatureFlags;
  /** Icon component for the nav item */
  icon: LucideIcon;
}

/**
 * Navigation items for the primary interaction modes.
 * FRONTEND-WEB-V1-R01: Support for Galen Chat, Semantic Search, Simple Search, Galen Search
 * FRONTEND-WEB-V1-R11: Each item is tied to a feature flag for runtime toggling
 * BROWSE-V1-R02: Browse entry sits alongside Chat and the Search modes,
 *                gated on flags.browse (which mirrors the deployment-time
 *                browse_enabled Pydantic Setting via BROWSE-V1-R01).
 */
const navigationItems: NavigationItem[] = [
  { label: 'Galen Chat', href: '/chat', featureKey: 'chat', icon: MessageSquareText },
  { label: 'Galen Search', href: '/search/hybrid', featureKey: 'galenSearch', icon: SearchCheck },
  { label: 'Semantic Search', href: '/search/semantic', featureKey: 'semanticSearch', icon: BrainCircuit },
  { label: 'Simple Search', href: '/search/simple', featureKey: 'simpleSearch', icon: Search },
  { label: 'Browse', href: '/browse', featureKey: 'browse', icon: BookOpen },
];

export function Layout(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { flags, isLoading } = useFeatureFlags();
  const { branding } = useBranding();
  const { resolvedTheme } = useTheme();
  const user = useUser();
  const logout = useAuthStore((state) => state.logout);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Sync fullscreen state with browser fullscreen changes (e.g. user presses Esc)
  useEffect(() => {
    const handler = (): void => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void document.documentElement.requestFullscreen();
    }
  }, []);

  /**
   * Check if a navigation item is currently active
   */
  const isActive = (href: string): boolean => {
    return location.pathname === href;
  };

  const isAdmin = user?.roles?.includes('admin') ?? false;

  /**
   * Filter navigation items based on feature flags
   * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules
   * FRONTEND-WEB-V1-R12: Fully hide disabled modules (not greyed out)
   */
  const visibleNavigationItems = useMemo(() => {
    return navigationItems.filter((item) => flags[item.featureKey]);
  }, [flags]);

  /**
   * FRONTEND-WEB-V1-R13: Responsive navigation link styles
   * Desktop: Smaller padding, inline layout
   * Mobile: Larger touch targets (44px minimum), full-width links
   */
  const getNavLinkClasses = (href: string, isMobile: boolean): string => {
    const baseClasses = 'font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 inline-flex items-center';
    const activeClasses = 'bg-accent text-accent-foreground';
    const inactiveClasses = 'text-muted-foreground hover:bg-accent hover:text-accent-foreground';

    if (isMobile) {
      // FRONTEND-WEB-V1-R13: Touch-friendly targets (min 44px height)
      return `${baseClasses} ${isActive(href) ? activeClasses : inactiveClasses} w-full px-4 py-3 text-base min-h-touch`;
    }
    return `${baseClasses} ${isActive(href) ? activeClasses : inactiveClasses} px-3 py-2 text-sm`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Skip link for keyboard navigation (FRONTEND-WEB-V1-R15) */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <header
        role="banner"
        className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      >
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-xl font-semibold hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded inline-flex items-center"
            >
              {branding.logoUrl ? (
                <img
                  src={(resolvedTheme === 'dark' && branding.logoDarkUrl) ? branding.logoDarkUrl : branding.logoUrl}
                  alt={branding.logoAltText}
                  className="h-8 max-w-[200px] object-contain"
                />
              ) : (
                branding.appName
              )}
            </Link>
          </div>
          {/* FRONTEND-WEB-V1-R01: Navigation to all four interaction modes */}
          {/* FRONTEND-WEB-V1-R11: Only show enabled modules based on feature flags */}
          {/* FRONTEND-WEB-V1-R12: Disabled modules are fully hidden, not greyed out */}
          {/* FRONTEND-WEB-V1-R13: Desktop nav (hidden on mobile) */}
          <nav
            role="navigation"
            aria-label="Main navigation"
            className="hidden md:block"
          >
            {isLoading ? (
              <div className="h-8 w-48 animate-pulse bg-muted rounded" aria-label="Loading navigation" />
            ) : (
              <ul className="flex items-center gap-1">
                {visibleNavigationItems.map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={getNavLinkClasses(item.href, false)}
                      aria-current={isActive(item.href) ? 'page' : undefined}
                    >
                      <item.icon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </nav>

          {/* Right side controls: user profile, theme toggle, mobile menu */}
          <div className="flex items-center gap-2">
            {/* AUTH-V1-R07: User profile indicator with dropdown menu */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                    aria-label={`User menu for ${user.email}`}
                  >
                    <span
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground"
                      aria-hidden="true"
                    >
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                    <span className="hidden lg:inline max-w-[120px] truncate">
                      {user.email}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isAdmin && flags.admin && (
                    <>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/configuration'); }}>
                        <Settings className="h-4 w-4 mr-2" aria-hidden="true" />
                        Configuration
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/widget-config'); }}>
                        <Settings className="h-4 w-4 mr-2" aria-hidden="true" />
                        Widget Configuration
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/speech-config'); }}>
                        <Mic className="h-4 w-4 mr-2" aria-hidden="true" />
                        Speech Configuration
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/users'); }}>
                        <Users className="h-4 w-4 mr-2" aria-hidden="true" />
                        Users
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/logs/resources'); }}>
                        <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                        Resource Logs
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/logs/safety'); }}>
                        <ShieldAlert className="h-4 w-4 mr-2" aria-hidden="true" />
                        Safety Logs
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/logs/queries'); }}>
                        <ScrollText className="h-4 w-4 mr-2" aria-hidden="true" />
                        Query Logs
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/usage'); }}>
                        <BarChart3 className="h-4 w-4 mr-2" aria-hidden="true" />
                        Usage Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/performance'); }}>
                        <Activity className="h-4 w-4 mr-2" aria-hidden="true" />
                        Performance
                      </DropdownMenuItem>
                      {/* REPORTING-V1-R12: admin reporting page */}
                      <DropdownMenuItem onSelect={() => { void navigate('/admin/reporting'); }}>
                        <FileBarChart className="h-4 w-4 mr-2" aria-hidden="true" />
                        Reporting
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onSelect={() => { logout(); void navigate('/login'); }}>
                    <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* FRONTEND-WEB-V1-R10: Theme toggle button */}
            <ThemeToggle />

            {/* FRONTEND-WEB-V1-R13: Mobile navigation (hamburger menu) */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open menu"
                  className="min-w-touch min-h-touch"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[350px]">
              <SheetHeader>
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              <nav role="navigation" aria-label="Mobile navigation" className="mt-6">
                {isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="h-12 animate-pulse bg-muted rounded"
                        aria-label="Loading"
                      />
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {visibleNavigationItems.map((item) => (
                      <li key={item.href}>
                        <SheetClose asChild>
                          <Link
                            to={item.href}
                            className={getNavLinkClasses(item.href, true)}
                            aria-current={isActive(item.href) ? 'page' : undefined}
                          >
                            <item.icon className="h-5 w-5 mr-2" aria-hidden="true" />
                            {item.label}
                          </Link>
                        </SheetClose>
                      </li>
                    ))}
                    {isAdmin && flags.admin && (
                      <>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/configuration"
                              className={getNavLinkClasses('/admin/configuration', true)}
                              aria-current={isActive('/admin/configuration') ? 'page' : undefined}
                            >
                              <Settings className="h-5 w-5 mr-2" aria-hidden="true" />
                              Configuration
                            </Link>
                          </SheetClose>
                        </li>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/widget-config"
                              className={getNavLinkClasses('/admin/widget-config', true)}
                              aria-current={isActive('/admin/widget-config') ? 'page' : undefined}
                            >
                              <Settings className="h-5 w-5 mr-2" aria-hidden="true" />
                              Widget Configuration
                            </Link>
                          </SheetClose>
                        </li>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/speech-config"
                              className={getNavLinkClasses('/admin/speech-config', true)}
                              aria-current={isActive('/admin/speech-config') ? 'page' : undefined}
                            >
                              <Mic className="h-5 w-5 mr-2" aria-hidden="true" />
                              Speech Configuration
                            </Link>
                          </SheetClose>
                        </li>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/users"
                              className={getNavLinkClasses('/admin/users', true)}
                              aria-current={isActive('/admin/users') ? 'page' : undefined}
                            >
                              <Users className="h-5 w-5 mr-2" aria-hidden="true" />
                              Users
                            </Link>
                          </SheetClose>
                        </li>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/logs/resources"
                              className={getNavLinkClasses('/admin/logs/resources', true)}
                              aria-current={isActive('/admin/logs/resources') ? 'page' : undefined}
                            >
                              <FileText className="h-5 w-5 mr-2" aria-hidden="true" />
                              Resource Logs
                            </Link>
                          </SheetClose>
                        </li>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/logs/safety"
                              className={getNavLinkClasses('/admin/logs/safety', true)}
                              aria-current={isActive('/admin/logs/safety') ? 'page' : undefined}
                            >
                              <ShieldAlert className="h-5 w-5 mr-2" aria-hidden="true" />
                              Safety Logs
                            </Link>
                          </SheetClose>
                        </li>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/logs/queries"
                              className={getNavLinkClasses('/admin/logs/queries', true)}
                              aria-current={isActive('/admin/logs/queries') ? 'page' : undefined}
                            >
                              <ScrollText className="h-5 w-5 mr-2" aria-hidden="true" />
                              Query Logs
                            </Link>
                          </SheetClose>
                        </li>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/usage"
                              className={getNavLinkClasses('/admin/usage', true)}
                              aria-current={isActive('/admin/usage') ? 'page' : undefined}
                            >
                              <BarChart3 className="h-5 w-5 mr-2" aria-hidden="true" />
                              Usage Dashboard
                            </Link>
                          </SheetClose>
                        </li>
                        <li>
                          <SheetClose asChild>
                            <Link
                              to="/admin/performance"
                              className={getNavLinkClasses('/admin/performance', true)}
                              aria-current={isActive('/admin/performance') ? 'page' : undefined}
                            >
                              <Activity className="h-5 w-5 mr-2" aria-hidden="true" />
                              Performance
                            </Link>
                          </SheetClose>
                        </li>
                      </>
                    )}
                  </ul>
                )}
              </nav>
            </SheetContent>
          </Sheet>
          </div>
        </div>
      </header>

      <main
        id="main-content"
        role="main"
        className="container mx-auto px-4 py-8"
        tabIndex={-1}
      >
        <Outlet />
      </main>

      <footer role="contentinfo" className="border-t py-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>
            {branding.disclaimers.footerText ?? `${branding.appName} - AI-Assisted Clinical Guideline Retrieval System`}
          </p>
          <p className="mt-1">{branding.disclaimers.footerRegulatory ?? 'EU MDR Class I Medical Device'}</p>
        </div>
      </footer>

      {/* Fullscreen toggle — fixed bottom-right */}
      <Button
        variant="outline"
        size="icon"
        className="fixed bottom-4 right-4 z-50 h-9 w-9 rounded-full bg-background/80 shadow-md backdrop-blur-sm"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        data-testid="fullscreen-toggle"
      >
        {isFullscreen ? (
          <Minimize2 className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Maximize2 className="h-4 w-4" aria-hidden="true" />
        )}
      </Button>
    </div>
  );
}
